// Where this copy of the widget runs: the Vertu-hosted booking page, or
// embedded in a venue's own website.
//
// The rule: the page is the HOSTED booking page when the script that is
// running (v1.js) came from the page's own origin. book.vertu.co.za, the
// staging vercel.app page and a venue's custom booking domain all load
// /v1.js from themselves (api/book.ts); an embed on a venue's site loads
// https://widget.vertu.co.za/v1.js from another origin.
//
// Worked out ONCE, while this module is evaluated: v1.js is a single IIFE
// (vite.config.ts), so every module body runs synchronously while
// document.currentScript is still that script. In any later callback or
// effect currentScript is null, which is why nothing here may be lazy.
//
// No currentScript (Vite's dev server serves ES modules, which have none; a
// loader that evals the bundle): hosted. That is how every page behaved
// before this file existed, so the embedded-only behaviour never switches on
// by accident.

/**
 * Is a script loaded from `scriptSrc` running on its own origin's page? An
 * absent or unreadable src counts as hosted (the safe default above).
 */
export function isHostedScript(
  scriptSrc: string | null | undefined,
  pageOrigin: string,
): boolean {
  if (!scriptSrc) return true;
  try {
    return new URL(scriptSrc, pageOrigin).origin === pageOrigin;
  } catch {
    return true;
  }
}

const embedded: boolean = (() => {
  if (typeof document === "undefined" || typeof location === "undefined") return false;
  try {
    const script = document.currentScript;
    // An SVG <script> has no src; only an HTML one can be the bundle.
    const src = script instanceof HTMLScriptElement ? script.src : null;
    return !isHostedScript(src, location.origin);
  } catch {
    return false;
  }
})();

/** Is the widget embedded in a venue's own page (not the hosted booking page)? */
export function isEmbedded(): boolean {
  return embedded;
}
